import { compose, translation, type Matrix } from '../geometry/matrix';
import { formatNumber, serializeSegments, transformSegments } from '../geometry/path';
import { inflateRect, type Rect } from '../geometry/rect';
import type { Design, NailShape, ProjectDocument, VectorNode } from '../model/types';
import { nailSilhouetteSegments } from '../nails/shapes';
import { evaluateDesign, evaluateNodes, type EvaluatedPath } from '../scene/evaluate';
import { attributesText, fillAttributes, paintAttributes } from './svg';
import { escapeXml } from './xml';

/**
 * Interface thumbnails for browsing (catalog tiles, project gallery). Rendered from the
 * same evaluated geometry as exports, but they are presentation images: lines are
 * thickened for legibility and nails may show a polish color. Never use these for
 * printing.
 */
const INK = '#2E2530';
const NAIL_EDGE = '#C9AEBB';
const f = (n: number) => formatNumber(n, 3);

function svg(viewBox: Rect, body: string, title: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${f(viewBox.x)} ${f(viewBox.y)} ${f(viewBox.width)} ${f(viewBox.height)}" fill="none"><title>${escapeXml(title)}</title>${body}</svg>\n`;
}

/**
 * Paint for one path inside a group that sets the browsing line style: filled shapes get
 * solid ink (and no line unless they have one); lines inherit the group's, optionally at
 * their own width.
 */
function inkedPath(p: EvaluatedPath, strokeWidthMm?: number): string {
  const line = !p.stroke
    ? ' stroke="none"'
    : strokeWidthMm === undefined
      ? ''
      : ` stroke-width="${f(strokeWidthMm)}"`;
  return `<path d="${p.d}"${attributesText(fillAttributes(p.fill, { ink: INK }))}${line}/>`;
}

function squareAround(r: Rect): Rect {
  const side = Math.max(r.width, r.height);
  return {
    x: r.x + r.width / 2 - side / 2,
    y: r.y + r.height / 2 - side / 2,
    width: side,
    height: side,
  };
}

export function motifThumbnail(nodes: readonly VectorNode[], bounds: Rect, title: string): string {
  const scene = evaluateNodes(nodes, { precision: 3 });
  const side = Math.max(bounds.width, bounds.height);
  const view = inflateRect(squareAround(bounds), side * 0.08);
  const paths = scene.paths.map((p) => inkedPath(p)).join('');
  return svg(
    view,
    `<g stroke="${INK}" stroke-width="${f(side * 0.022)}" stroke-linecap="round" stroke-linejoin="round">${paths}</g>`,
    title,
  );
}

/**
 * Preview of placed art with its real millimeter line widths and colors (for example
 * the SVG import dialog), framed by its visual bounds.
 */
export function artPreviewSvg(nodes: readonly VectorNode[], title: string): string | null {
  const scene = evaluateNodes(nodes, { precision: 3 });
  if (!scene.visualBounds) return null;
  const side = Math.max(scene.visualBounds.width, scene.visualBounds.height);
  const view = inflateRect(squareAround(scene.visualBounds), side * 0.06);
  const paths = scene.paths
    .map((p) => `<path d="${p.d}"${attributesText(paintAttributes(p, { precision: 3 }))}/>`)
    .join('');
  return svg(view, paths, title);
}

export interface NailFrame {
  readonly shape: NailShape;
  readonly widthMm: number;
  readonly lengthMm: number;
  readonly placement: Matrix;
  /** Polish color shown in the thumbnail (presentation only). */
  readonly fill?: string;
}

export interface NailArtOptions {
  /**
   * Keep each line's own weight (slightly thickened) instead of one thicker browsing
   * width: detailed designs have lines close enough to merge at 0.24 mm (ADR 0015).
   */
  readonly ownLineWeights?: boolean;
}

/** Thickening applied to a detailed design's own line weights in thumbnails. */
const OWN_WEIGHT_SCALE = 1.25;

/** One nail: silhouette, clipped line art, and a soft outline, offset into the image. */
export function nailArtSvg(
  design: Design,
  nail: NailFrame,
  offset: Matrix,
  clipId: string,
  options: NailArtOptions = {},
): string {
  const silhouette = serializeSegments(
    transformSegments(nailSilhouetteSegments(nail.shape, nail.widthMm, nail.lengthMm), offset),
    3,
  );
  const scene = evaluateDesign(design, { outer: compose(offset, nail.placement), precision: 3 });
  const paths = scene.paths
    .map((p) =>
      inkedPath(
        p,
        options.ownLineWeights && p.stroke ? p.stroke.widthMm * OWN_WEIGHT_SCALE : undefined,
      ),
    )
    .join('');
  return (
    `<defs><clipPath id="${clipId}"><path d="${silhouette}"/></clipPath></defs>` +
    `<path d="${silhouette}" fill="${nail.fill ?? '#FFFFFF'}" stroke="${NAIL_EDGE}" stroke-width="0.25"/>` +
    `<g clip-path="url(#${clipId})" stroke="${INK}" stroke-width="0.24" stroke-linecap="round" stroke-linejoin="round">${paths}</g>`
  );
}

export function singleNailThumbnail(
  design: Design,
  nail: NailFrame,
  title: string,
  options: NailArtOptions = {},
): string {
  const pad = 1.5;
  const body = nailArtSvg(design, nail, translation(pad, pad), 'clip', options);
  return svg(
    { x: 0, y: 0, width: nail.widthMm + 2 * pad, height: nail.lengthMm + 2 * pad },
    body,
    title,
  );
}

/** Row of a project's nails (bottoms aligned), or its first design's artboard if it has none. */
export function projectThumbnail(
  project: ProjectDocument,
  options: { showPolish?: boolean } = {},
): string {
  if (project.nails.length === 0) {
    const design = project.designs[0]!;
    const scene = evaluateDesign(design, { precision: 3 });
    const paths = scene.paths.map((p) => inkedPath(p)).join('');
    const { widthMm: w, heightMm: h } = design.artboard;
    return svg(
      { x: -1, y: -1, width: w + 2, height: h + 2 },
      `<rect width="${f(w)}" height="${f(h)}" fill="#FFFFFF" stroke="${NAIL_EDGE}" stroke-width="0.2"/><g stroke="${INK}" stroke-width="0.24" stroke-linecap="round" stroke-linejoin="round">${paths}</g>`,
      project.name,
    );
  }
  const gap = 2;
  const pad = 1.5;
  const maxLength = Math.max(...project.nails.map((n) => n.lengthMm));
  let x = pad;
  const parts: string[] = [];
  project.nails.forEach((nail, i) => {
    const design = project.designs.find((d) => d.id === nail.designId);
    if (!design) return;
    const offset = translation(x, pad + (maxLength - nail.lengthMm));
    parts.push(
      nailArtSvg(
        design,
        {
          shape: nail.shape,
          widthMm: nail.widthMm,
          lengthMm: nail.lengthMm,
          placement: nail.placement,
          ...(options.showPolish ? { fill: nail.baseColor } : {}),
        },
        offset,
        `clip-${i}`,
      ),
    );
    x += nail.widthMm + gap;
  });
  return svg(
    { x: 0, y: 0, width: x - gap + pad, height: maxLength + 2 * pad },
    parts.join(''),
    project.name,
  );
}
